import * as Hangul from 'hangul-js'
import { difference, sortBy } from 'lodash-es'
import { makeAutoObservable } from 'mobx'

import { EMPTY_STRING_ID } from '@/constant/common'
import type { SmsStudent, SmsStudentGroupColl } from '@/dto'
import CheckSet from '@/utils/models/CheckSet'

import type { SchoolGrade } from '../Student/SchoolGrade'
/***
 * @see StudentController
 ***/
export class SmsStudentGroup {
  private _id: number
  private _name = ''
  grade: SchoolGrade['grade']
  schoolType: SchoolGrade['schoolType'] | typeof EMPTY_STRING_ID
  memberList: SmsStudent[]
  recipient: SmsStudentGroupColl['recipient']
  memberSet: CheckSet<SmsStudent>

  constructor(data) {
    makeAutoObservable(this)
    this._id = data.id
    this._name = data.name
    this.memberList = data.memberList
    this.grade = data.grade
    this.schoolType = data.schoolType ?? EMPTY_STRING_ID
    this.recipient = data.recipient ?? '학부모'

    if (data instanceof SmsStudentGroup) {
      this.memberSet = new CheckSet<SmsStudent>(
        data.memberSet.allList,
        data.memberSet.toArray,
        data.memberSet.disabledList,
      )
    } else {
      this.memberSet = this.makeMemberSet()
    }
  }

  get flatGrade() {
    const elemGrade: 0 | 6 | 9 = (() => {
      switch (this.schoolType) {
        case 'ELEMENTARY':
          return 0
        case 'MIDDLE':
          return 6
        case 'HIGH':
          return 9
        default:
          return 0
      }
    })()
    return (elemGrade + this.grade) as SchoolGrade['flatGrade']
  }

  get koreanSchoolType() {
    switch (this.schoolType) {
      case 'ELEMENTARY':
        return '초'
      case 'MIDDLE':
        return '중'
      case 'HIGH':
        return '고'
      default:
        return ''
    }
  }

  get id() {
    return this._id === undefined ? this.flatGrade : this._id
  }

  get name() {
    return this._name === undefined ? `${this.koreanSchoolType}${this.grade}` : this._name
  }

  get groupType() {
    return this.schoolType === EMPTY_STRING_ID ? 'CLASS' : 'GRADE'
  }

  get toUnCheckedArray() {
    return sortBy(this.memberSet.toUnCheckedArray, [(student) => student.name])
  }

  get toCheckedArray() {
    return sortBy(this.memberSet.toArray, [(student) => student.name])
  }

  get isFullyChecked() {
    return this.memberSet.isFullyChecked
  }

  get isPartChecked() {
    return this.memberSet.isPartChecked
  }

  get initialActiveSet(): Set<SmsStudent> {
    return new Set<SmsStudent>(difference(this.memberSet.allList, this.memberSet.disabledList))
  }

  get initialActiveList(): SmsStudent[] {
    return [...this.initialActiveSet.values()]
  }

  get checkSize() {
    return this.memberSet.checkSize
  }

  makeMemberSet() {
    const disableList = this.memberList.filter((member) => {
      if (this.recipient === '학생') {
        return !member.phone
      } else {
        return !member.parent.phone
      }
    })
    return new CheckSet(this.memberList, [], disableList)
  }

  changeRecipient(recipient: SmsStudentGroupColl['recipient']) {
    this.recipient = recipient
    this.memberSet = this.makeMemberSet()
  }

  searchName(searchValue: string) {
    const searcher = new Hangul.Searcher(searchValue)
    let disableCount = 0
    const itemArray = this.toUnCheckedArray.filter((student) => {
      const isIncluded = searcher.search(student['name']) !== -1
      const phone = this.recipient === '학부모' ? student.parent.phone : student.phone
      if (isIncluded && !phone) {
        disableCount++
      }
      return isIncluded
    })
    return { itemArray, disableCount }
  }

  getActiveMember(student: SmsStudent): SmsStudent | undefined {
    return this.initialActiveList.find((member) => member.id === student.id)
  }

  isChecked(student) {
    return this.memberSet.isChecked(student)
  }

  setCheck(student: SmsStudent) {
    const member = this.getActiveMember(student)
    if (member) {
      this.memberSet.setCheck(member)
    }
  }

  deleteCheck(student: SmsStudent) {
    const member = this.getActiveMember(student)
    if (member) {
      this.memberSet.deleteCheck(member)
    }
  }

  allCheck() {
    this.initialActiveList.forEach((student) => {
      this.setCheck(student)
    })
  }

  deleteAllCheck() {
    this.initialActiveList.forEach((student) => {
      this.deleteCheck(student)
    })
  }

  clearAllCheck() {
    this.memberSet.clearAllCheck()
  }
}
